import { isReelLike } from '@/helpers/isReelLike';

export const AUTO_SCROLL_MODE = {
  disabled: 'disabled',
  stopAtEnd: 'stop_at_end',
  continuous: 'continuous',
};

const CIRCLE_READ_STATUS_BORDER_PADDING = 3;
const DEFAULT_CIRCLE_READ_STATUS_OUTER_OFFSET = 14;

// タイル型のレイアウト定数（行内タイル幅の上限と既定 gap）。icons.js の行詰めロジックと
// getCampaignTileBlockMaxWidth の両方が同じ値を参照する必要があるため、ここを唯一の定義箇所とする
export const TILE_MAX_WIDTH = 190;
export const TILE_DEFAULT_GAP = 10;

const normalizeAutoScrollMode = (raw) => {
  if (raw == null) return null;

  if (typeof raw === 'boolean') {
    // boolean化された auto_scroll_mode: true=ループ, false=無効
    return raw ? AUTO_SCROLL_MODE.continuous : AUTO_SCROLL_MODE.disabled;
  }

  if (typeof raw === 'number') {
    if (raw === 0) return AUTO_SCROLL_MODE.disabled;
    if (raw === 1) return AUTO_SCROLL_MODE.stopAtEnd;
    if (raw === 2) return AUTO_SCROLL_MODE.continuous;
    return null;
  }

  const s = String(raw).trim();
  if (!s) return null;

  if (s === 'stopAtEnd') return AUTO_SCROLL_MODE.stopAtEnd;
  const v = s.toLowerCase().replace(/-/g, '_');

  if (v === AUTO_SCROLL_MODE.disabled) return AUTO_SCROLL_MODE.disabled;
  if (v === AUTO_SCROLL_MODE.stopAtEnd) return AUTO_SCROLL_MODE.stopAtEnd;
  if (v === AUTO_SCROLL_MODE.continuous) return AUTO_SCROLL_MODE.continuous;
  if (v === 'true') return AUTO_SCROLL_MODE.continuous;
  if (v === 'false') return AUTO_SCROLL_MODE.disabled;

  return null;
};

export const getCampaignAutoScrollMode = (campaign) => {
  const raw = campaign?.autoScrollMode ?? campaign?.auto_scroll_mode;
  const mode = normalizeAutoScrollMode(raw);
  if (mode) return mode;

  // 後方互換: 旧カラムが残っている場合の暫定マッピング
  if (typeof campaign?.autoScrollEnabled === 'boolean') {
    return campaign.autoScrollEnabled ? AUTO_SCROLL_MODE.continuous : AUTO_SCROLL_MODE.disabled;
  }

  return AUTO_SCROLL_MODE.disabled;
};

export const isCampaignLoopPlaybackEnabled = (campaign) => {
  const raw = campaign?.loopPlaybackEnabled ?? campaign?.loop_playback_enabled;

  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 't'].includes(v)) return true;
    if (['false', '0', 'no', 'n', 'f'].includes(v)) return false;
  }

  // 既存挙動（squareは常にloop、circleは状況次第）を壊しにくいようデフォルトはtrue
  return true;
};

export const isCampaignAutoPlayEnabled = (campaign) => {
  const raw =
    campaign?.autoPlayEnabled
    ?? campaign?.auto_play_enabled
    ?? campaign?.autoPlaybackEnabled
    ?? campaign?.auto_playback_enabled
    ?? campaign?.autoPlayNextEnabled
    ?? campaign?.auto_play_next_enabled;

  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 't'].includes(v)) return true;
    if (['false', '0', 'no', 'n', 'f'].includes(v)) return false;
  }

  // 既存挙動を維持するため、デフォルトはtrue
  return true;
};

export const getCampaignGroupGap = (campaign) => {
  const raw = campaign?.groupGap ?? campaign?.group_gap;
  if (raw == null || raw === '') return null;

  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return null;

  return value;
};

export const getCampaignReadStatusBorderWidth = (campaign) => {
  const raw = campaign?.readStatusBorderWidth ?? campaign?.read_status_border_width;
  if (raw == null || raw === '') return 4;

  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1 || value > 20) return 4;

  return value;
};

export const getCampaignCircleItemWidth = (campaign) => {
  const showReadStatus = campaign?.isReadStatusVisible === true;
  const readStatusBorderWidth = getCampaignReadStatusBorderWidth(campaign);
  const groupGap = getCampaignGroupGap(campaign);
  const shouldCollapseCircleItemWidth = campaign?.frame === 'circle'
    && !showReadStatus
    && groupGap != null
    && groupGap < DEFAULT_CIRCLE_READ_STATUS_OUTER_OFFSET;

  if (shouldCollapseCircleItemWidth) {
    // 外枠なしでは設定値を見た目上の距離として扱い、gapを使い切ったら幅を詰める
    return `calc(var(--storeeel-circle-thumbnail-size, 80px) + ${groupGap}px)`;
  }

  const circleOuterWidth = showReadStatus
    // 外枠ありではCSS変数から外径を計算し、再描画時の太さ変更にも追従させる
    ? `calc(var(--storeeel-circle-thumbnail-size, 80px) + var(--storeeel-read-status-ring-size, ${readStatusBorderWidth}px) + var(--storeeel-read-status-ring-size, ${readStatusBorderWidth}px) + ${2 * CIRCLE_READ_STATUS_BORDER_PADDING}px)`
    : `calc(var(--storeeel-circle-thumbnail-size, 80px) + ${DEFAULT_CIRCLE_READ_STATUS_OUTER_OFFSET}px)`;

  return `max(var(--storeeel-group-name-width, 92px), ${circleOuterWidth})`;
};

export const CUSTOM_HTML_POSITION = {
  after: 'after',
  before: 'before',
};

const normalizeCustomHtmlPosition = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (s === CUSTOM_HTML_POSITION.before) return CUSTOM_HTML_POSITION.before;
  if (s === CUSTOM_HTML_POSITION.after) return CUSTOM_HTML_POSITION.after;
  return null;
};

export const getCampaignCustomHtmlPosition = (campaign) => {
  const raw = campaign?.customHtmlPosition ?? campaign?.custom_html_position;
  // 未設定・不正値・API未反映期間は既存互換の after にフォールバック
  return normalizeCustomHtmlPosition(raw) ?? CUSTOM_HTML_POSITION.after;
};

export const getRenderedCampaignGroupGap = (campaign) => {
  const groupGap = getCampaignGroupGap(campaign);
  if (groupGap == null) return null;

  const isCircleWithoutReadStatus = campaign?.frame === 'circle' && campaign?.isReadStatusVisible !== true;
  if (isCircleWithoutReadStatus) {
    // ストーリー型の外枠OFFでは、設定値をサムネイル同士の見た目上の距離として扱う
    return Math.max(groupGap - 14, 0);
  }

  if (campaign?.frame === 'tile') {
    // タイル型はサムネイルに左右marginを持たない（.storeeel-tile-row .storeeel-content で margin:0 に上書き）ため、
    // リール型のような補正はせず設定値をそのままgapとして使う
    return groupGap;
  }

  if (isReelLike(campaign?.frame)) {
    // リール型はサムネイル左右marginの合計10pxを先に使い、超過分だけgapにする
    return Math.max(groupGap - 10, 0);
  }

  return groupGap;
};

// タイル型の行間（縦方向gap）を取得する。group_gap（横方向）とは意味が異なるため独立関数とする
export const getCampaignTileGapVertical = (campaign) => {
  const raw = campaign?.tileGapVertical ?? campaign?.tile_gap_vertical;
  if (raw == null || raw === '') return 10;

  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return 10;

  return value;
};

// タイル型: 選択グリッドの列数（camelCase/snake_case 両対応、既定 4）。
// icons.js の行詰めロジック（generateTileRows）と必ず同じ式を使うこと
export const getCampaignTileColumns = (campaign) => (
  campaign?.tileColumns || campaign?.tile_columns || 4
);

// タイル型: 選択グリッドの行数（camelCase/snake_case 両対応、既定 2）。
// フィードフォルダの表示件数強制（tileRows × tileColumns）で使用する（feedFolderResolver.js）
export const getCampaignTileRows = (campaign) => {
  const raw = campaign?.tileRows ?? campaign?.tile_rows;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 2;
};

// タイル型の高さは常にこの値で固定する（既定9:16・最大幅190px時の高さと同じ値にすることで、
// 既定設定の見た目は変えない）。幅を固定して高さをアスペクト比から可変にすると、横の比率を
// 大きくする（＝正方形・横長に近づける）たびにタイルが縦に潰れて小さく見えてしまうため、
// 逆に高さを固定し、幅をアスペクト比から算出する（列の持ち分までは幅が可変）方式にしている。
export const TILE_REFERENCE_HEIGHT = TILE_MAX_WIDTH * (16 / 9);

// タイル1枚あたりの「アスペクト比と固定高さ(TILE_REFERENCE_HEIGHT)から求まる自然な幅」(px)。
// 列の持ち分（後述のtileWidth計算式側でmin()により頭打ち）を考慮しない、純粋な理想幅。
export const getCampaignTileNaturalWidth = (campaign) => {
  const width = Number(campaign?.tileAspectWidth ?? campaign?.tile_aspect_width ?? 9);
  const height = Number(campaign?.tileAspectHeight ?? campaign?.tile_aspect_height ?? 16);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return TILE_REFERENCE_HEIGHT * (9 / 16);
  }
  return TILE_REFERENCE_HEIGHT * (width / height);
};

// タイル型グリッド全体の自然な最大幅（列数 × タイル1枚あたりの自然な幅 + 列間gap）。
// icons.js の tileWidth 計算式（min(getCampaignTileNaturalWidth, ...)）と同じ前提を使い、行コンテナ
// (.storeeell_base_X) の max-width をこの値でキャップする。これによりグリッドの自然幅が
// 表示領域より狭い場合に、キャンペーン全体を（外側のPC中央寄せ/モバイル左寄せ処理で）中央寄せできる
export const getCampaignTileBlockMaxWidth = (campaign) => {
  const columns = getCampaignTileColumns(campaign);
  const gap = getRenderedCampaignGroupGap(campaign) ?? TILE_DEFAULT_GAP;
  return columns * getCampaignTileNaturalWidth(campaign) + (columns - 1) * gap;
};
